import { IUserPlanRequestsState, UserPlanRequestStatus } from '@/finsight/application/User/UserPlan/helpers';
import { UserPlanType, UserPlanTypePrice } from '@/users/domain/UserPlan';
import { IPlanPanel } from './IPlanPanel';
import { ActionType } from '@/finsight/domain/Pricing/ActionType';

import styles from '../styles.scss';

const UNAUTHORIZED_BUTTON_CONFIG = {
  [UserPlanType.STARTER]: {
    buttonText: 'Log In for FREE',
    isButtonFill: false,
    isButtonDisable: false,
    actionType: ActionType.LOGIN,
  },
  [UserPlanType.PREMIUM]: {
    buttonText: 'Upgrade Plan',
    isButtonFill: true,
    isButtonDisable: false,
    actionType: ActionType.LOGIN_AND_REQUEST,
  },
  [UserPlanType.PRO]: {
    buttonText: 'Upgrade Plan',
    isButtonFill: true,
    isButtonDisable: false,
    actionType: ActionType.LOGIN_AND_REQUEST,
  },
};

const AUTHORIZED_BUTTON_CONFIG = {
  [UserPlanType.STARTER]: {
    buttonText: 'You are logged-in',
    isButtonFill: false,
    isButtonDisable: true,
    actionType: ActionType.NONE,
  },
  [UserPlanType.PREMIUM]: {
    buttonText: 'Upgrade Plan',
    isButtonFill: true,
    isButtonDisable: false,
    actionType: ActionType.REQUEST,
  },
  [UserPlanType.PRO]: {
    buttonText: 'Upgrade Plan',
    isButtonFill: true,
    isButtonDisable: false,
    actionType: ActionType.REQUEST,
  },
};

const REQUEST_BUTTON_CONFIG = {
  [UserPlanRequestStatus.Pending]: {
    buttonText: 'Pending',
    isButtonFill: true,
    isButtonDisable: true,
    actionType: ActionType.NONE,
  },
  [UserPlanRequestStatus.Denied]: {
    buttonText: 'Upgrade Denied',
    isButtonFill: false,
    isButtonDisable: true,
    actionType: ActionType.NONE,
  },
};

const getButtonConfig = (
  plan: UserPlanType,
  currentPlan: UserPlanType,
  requests: IUserPlanRequestsState,
) => {
  const request = requests[plan];

  if (plan === currentPlan) {
    return {
      buttonText: 'Current Plan',
      isButtonFill: false,
      isButtonDisable: true,
      isCurrent: true,
    };
  }

  if (currentPlan === UserPlanType.UNAUTHORIZED) {
    return UNAUTHORIZED_BUTTON_CONFIG[plan];
  }

  if (request && REQUEST_BUTTON_CONFIG[request.status]) {
    return REQUEST_BUTTON_CONFIG[request.status];
  }

  if (
    (plan === UserPlanType.PREMIUM && requests[UserPlanType.PRO]?.status === UserPlanRequestStatus.Pending)
    || (plan === UserPlanType.PRO && requests[UserPlanType.PREMIUM]?.status === UserPlanRequestStatus.Pending)
  ) {
    return { ...AUTHORIZED_BUTTON_CONFIG[plan], isButtonDisable: true };
  }

  if (plan === UserPlanType.PREMIUM && currentPlan === UserPlanType.PRO) {
    return { ...AUTHORIZED_BUTTON_CONFIG[plan], isButtonDisable: true };
  }

  return AUTHORIZED_BUTTON_CONFIG[plan];
};

export const basePlansConfig = [
  {
    title: 'Starter',
    description: 'Limited access to new issue deal data and visualizations - for free.',
    price: UserPlanTypePrice[UserPlanType.STARTER],
    pricePeriod: '',
    priceDescription: 'No additional fees',
    dataTest: 'Starter',
    className: styles.planPanelStarter,
    plan: UserPlanType.STARTER,
  },
  {
    title: 'Premium',
    headTitle: 'For FINSIGHT Clients & Institutional Investors.',
    description: 'Unlimited access to new issue deal data & visualizations, league tables, rating agency reports and more for one credit product and one region.',
    price: `$ ${ UserPlanTypePrice[UserPlanType.PREMIUM] }`,
    pricePeriod: '/ Month',
    priceDescription: 'Billed Annually',
    dataTest: 'Plus',
    className: styles.planPanelPlus,
    plan: UserPlanType.PREMIUM,
  },
  {
    title: 'Professional',
    headTitle: 'For FINSIGHT Clients & Institutional Investors.',
    description: 'Everything on Premium, plus exclusive access to our award-winning BWIC & Inventory Monitor, TRACE, Portfolio Monitor, and Bond Screener modules for one credit product and one region.',
    price: `$ ${ UserPlanTypePrice[UserPlanType.PRO] }`,
    pricePeriod: '/ Month',
    priceDescription: 'Billed Annually',
    dataTest: 'Pro',
    className: styles.planPanelPro,
    plan: UserPlanType.PRO,
  },
];

export const CURRENT_USER_PLAN_INDICATOR = 'your current plan';

const getPlansConfig = (
  currentPlan: UserPlanType,
  requests: IUserPlanRequestsState,
): IPlanPanel[] => basePlansConfig.map((planConfig) => ({
    ...planConfig,
    ...getButtonConfig(planConfig.plan, currentPlan, requests),
  }));

export default getPlansConfig;
